// @flow
import PRICE_TYPE from '../../../constants/priceType'
import { config } from '../../../config'

// Types
import type { Price } from '../../../models/price'
import type {
  Currency,
  PriceFixed,
  PriceCost,
  PriceEstimate,
} from '../../../api_types'

type PriceData = {
  price?: PriceFixed,
  cost?: PriceCost,
  estimates?: PriceEstimate,
}

/**
 * Returns a Price object by price object and currency object
 * from search results v2 (prebook or on-demand). Basically the first object
 * could be anything that have `price`, `cost` or `estimates` fields.
 * @param  {Object} price Price infortmation
 * @param  {Object} price Currency infortmation
 * @return {Object} Pasred price object
 * @private
 */
function tripPriceBuilder(
  { price, cost, estimates }: PriceData,
  currency?: Currency
): Price {
  const priceCurrency =
    (cost && cost.currency) || (estimates && estimates.currency)
  const finalCurrency = currency ||
    priceCurrency || {
      code: '',
      sign: '',
    }

  const currencyData = {
    currencyCode: finalCurrency.code,
    currencySymbol: finalCurrency.sign,
  }

  const priceData = {
    total: '',
    slash: '',
    raw: 0,
    type: PRICE_TYPE.NO_PRICE,
    platformFee: '',
    totalWithoutPlatformFee: '',
  }

  if (price && price.total_price) {
    priceData.total = config.NO_COMPACT_PRICE
      ? price.total_price.display
      : price.total_price.compact
    priceData.slash = price.slashed_price ? price.slashed_price.compact : ''
    priceData.raw = parseFloat(price.total_price.value)
    priceData.type = PRICE_TYPE.FIXED_PRICE
  } else if (estimates && estimates.min && estimates.max) {
    if (
      estimates.min.compact &&
      estimates.min.compact === estimates.max.compact
    ) {
      priceData.total = `~${estimates.min.compact}`
      priceData.raw = parseFloat(estimates.min.value)
      priceData.type = PRICE_TYPE.ESTIMATE_PRICE
    } else {
      priceData.total = `${estimates.min.compact} - ${estimates.max.compact}`
      priceData.raw = parseFloat(estimates.min.value)
      priceData.type = PRICE_TYPE.PRICE_RANGE
    }
  } else if (cost && cost.base_fare) {
    priceData.total = `min. ${cost.base_fare.compact}`
    priceData.raw = parseFloat(cost.base_fare.value)
    priceData.type = PRICE_TYPE.BASE_PRICE
  }

  if (price && price.platform_fee) {
    priceData.platformFee = price.platform_fee.compact
  }
  if (price && price.total_price_without_platform_fee) {
    priceData.totalWithoutPlatformFee = config.NO_COMPACT_PRICE
      ? price.total_price_without_platform_fee.display
      : price.total_price_without_platform_fee.compact
  }
  if (!priceData.totalWithoutPlatformFee) {
    priceData.totalWithoutPlatformFee = priceData.total
  }

  return {
    ...currencyData,
    ...priceData,
  }
}

export default tripPriceBuilder
